import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-sonner-toast';
import { useGameState } from '@/contexts/GameStateContext';
import { Loader2 } from 'lucide-react';
import { Element as ElementType } from '@/types';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { db } from '@/lib/firebase/firestore';
import { addDoc, collection } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';

interface DeleteElementModalProps {
  isOpen: boolean;
  onClose: () => void;
  element: ElementType | null;
}

export function DeleteElementModal({ isOpen, onClose, element }: DeleteElementModalProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const { toast } = useToast();
  const { deleteElement } = useGameState();
  const { currentUser } = useAuth();

  // Check if the element is one of the starting elements
  const isStartingElement = element && ['Water', 'Fire', 'Dirt', 'Air'].includes(element.name);

  const handleDelete = async () => {
    if (!element) {
      toast({
        title: 'No element selected',
        description: 'Please select an element to delete.',
        variant: 'destructive',
      });
      return;
    }

    if (isStartingElement) {
      toast({
        title: 'Cannot Delete',
        description: 'The starting elements cannot be deleted.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Delete the element
      deleteElement(element.id);

      // Submit the report with the reason
      if (reason.trim() && currentUser) {
        await addDoc(collection(db, 'help_reports'), {
          type: 'element_deletion',
          userId: currentUser.uid,
          userEmail: currentUser.email,
          elementName: element.name,
          elementId: element.id,
          reason: reason,
          timestamp: new Date(),
          status: 'new',
          category: 'element',
        });
      }

      toast({
        title: 'Element Deleted',
        description: `"${element.name}" has been removed from your elements.`,
        variant: 'default',
      });

      // Close modal and reset reason
      setReason('');
      onClose();
    } catch (error) {
      console.error('Error deleting element:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete element. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Delete Element</DialogTitle>
          <DialogDescription>
            {element ? (
              isStartingElement ? (
                <>
                  <strong>{element.name}</strong> is a starting element and cannot be deleted.
                </>
              ) : (
                <>
                  Are you sure you want to delete <strong>{element.name}</strong>? This will remove
                  it from both Elements & Discoveries.
                </>
              )
            ) : (
              'Please select an element to delete.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {element && (
            <>
              <div className="flex items-center justify-center p-4 bg-muted rounded-md">
                <div
                  className="px-3 py-1.5 rounded-xl text-center"
                  style={{
                    backgroundColor: element.color || '#888888',
                    color: '#ffffff',
                  }}
                >
                  {element.name}
                </div>
              </div>

              {!isStartingElement && (
                <div className="space-y-2">
                  <Label htmlFor="reason">Why are you deleting this element? (optional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="e.g., Duplicate, inappropriate, etc."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="resize-none"
                    rows={3}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isSubmitting || !element || isStartingElement}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete Element'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
